/**
 * pageTypes.js — what the page list offers for the chosen format.
 *
 * The operator picks a FORMAT — one of the team's templates — and the list
 * offers that template's own pages, in the template's order (formats.js). Each
 * is built by an existing recipe, or by a generic page carrying the template's
 * caption presets, or is marked as made in GIMP.
 *
 * Under them, "Other pages": the generic types, which work in any format, and
 * any of the format's recipes that no template page uses.
 *
 * `block` is checked against a real draft in pageTypes.test.js, so the
 * declaration cannot drift from the code. A text, info or ceph page is never
 * repeatable — a second Agreement is always a mistake.
 */
import { RECIPES } from './draft.js'
import { ROLE_LABELS } from './classify.js'
import { GENERIC_PAGES, genericPage } from './genericPages.js'
import { formatFor, recipesKeyFor } from './formats.js'
import { formatPageTypes } from './formatPages.js'

/**
 * Per recipe set, per recipe id. Page order is NOT declared here — for a
 * template page it is the format's order, and for an unplaced recipe it is the
 * recipe array's.
 */
export const PAGE_TYPE_DECLARATIONS = {
  orthognathic: {
    agreement: { block: 'text', repeatable: false },
    'source-data': { block: 'info', repeatable: false },
    cephalometry: { block: 'ceph', repeatable: false },
    preop: { block: 'grid', repeatable: true },
    comparison: { block: 'grid', repeatable: false },
    postplan: { block: 'grid', repeatable: true },
    'splint-intermediate': { block: 'grid', repeatable: true },
    'splint-final': { block: 'grid', repeatable: true },
    'splint-movement': { block: 'grid', repeatable: true },
    'trial-fitment': { block: 'grid', repeatable: true },
    // One type, possibly several sheets (`print-models`, `print-models#1`). Not
    // repeatable: a second instance would print every model twice.
    'print-models': { block: 'gallery', repeatable: false },
    'terms-1': { block: 'text', repeatable: false },
    'terms-2': { block: 'text', repeatable: false },
  },
  fibula: {
    agreement: { block: 'text', repeatable: false },
    'native-skull': { block: 'grid', repeatable: true },
    'resection-area': { block: 'grid', repeatable: true },
    'mandible-resection-guides': { block: 'grid', repeatable: true },
    'reconstructed-mandible': { block: 'grid', repeatable: true },
    'post-plan-skull': { block: 'grid', repeatable: true },
    'terms-1': { block: 'text', repeatable: false },
    'terms-2': { block: 'text', repeatable: false },
  },
  orbital: {
    preview: { block: 'info', repeatable: false },
    'native-skull': { block: 'grid', repeatable: true },
    'orbital-volumes': { block: 'gallery', repeatable: false },
    'bilateral-orbital': { block: 'grid', repeatable: false },
    'difference-model': { block: 'grid', repeatable: false },
    'difference-on-trial': { block: 'gallery', repeatable: false },
  },
}

/** A recipe with what the page list needs: which block it builds, and whether it repeats. */
function declared(recipe, setKey) {
  const decl = PAGE_TYPE_DECLARATIONS[setKey]?.[recipe.recipeId ?? recipe.id]
  if (!decl) throw new Error(`pageTypes: recipe "${setKey}/${recipe.id}" has no declaration`)
  return { ...recipe, ...decl }
}

/**
 * Every page of the format's template, in the template's order — including the
 * ones made in GIMP, which are listed and cannot be added.
 */
export function templatePages(formatId) {
  const setKey = recipesKeyFor(formatId)
  return formatPageTypes(formatId, RECIPES).map((t) => {
    if (t.hand) return t
    if (t.genericId) return t                    // a generic page carries its own declaration
    return declared(t, t.recipeSet ?? setKey)
  })
}

/** One recipe set, with each recipe's declaration — what the block tests check. */
export const declaredRecipes = (setKey) =>
  (RECIPES[setKey] ?? []).map((r) => declared(r, setKey))

/** The template's pages that the Builder can build — what "add every page" adds. */
export const pageTypesFor = (formatId) => templatePages(formatId).filter((t) => !t.hand)

/** The format's recipes that no page of its template uses — offered under "Other pages". */
export function unplacedRecipes(formatId) {
  const setKey = recipesKeyFor(formatId)
  if (!setKey) return []
  const used = new Set(templatePages(formatId).map((t) => t.recipeId).filter(Boolean))
  return (RECIPES[setKey] ?? [])
    .filter((r) => !used.has(r.id))
    .map((r) => declared(r, setKey))
}

/**
 * Any page type the operator can add: a generic one, one of the format's
 * template pages, or one of its unplaced recipes. Also resolves a page type
 * named by a stored list.
 */
export const pageType = (formatId, id) =>
  genericPage(id)
  ?? templatePages(formatId).find((t) => t.id === id)
  ?? unplacedRecipes(formatId).find((t) => t.id === id)

/** What "Add a page" offers: this template's pages, then the generic ones. */
export const offeredPages = (formatId) => ({
  template: templatePages(formatId),
  // `formatOnly` types are reached through a format's own page (`use:`), never from this list.
  generic: GENERIC_PAGES.filter((t) => !t.formatOnly),
  other: unplacedRecipes(formatId),
  held: formatFor(formatId)?.held ?? null,
})

/**
 * The mesh slots a suggested page asks the library for — the rows its per-page
 * mesh choice offers.
 *
 * Derived from the recipe's own `needs` / `needsAny` / `wants`, keyed
 * `role/stage` exactly as `picks` already is. Returns null for a `custom`
 * recipe, whose slots are recorded at draft time from the calls its compose()
 * actually makes.
 */
export function slotsFor(type) {
  if (type.kind === 'custom') return null
  const rows = []
  const push = ([role, stage], required, group) => rows.push({
    key: `${role}/${stage}`, role, stage, required, group,
    label: `${ROLE_LABELS[role] ?? role} (${stage})`,
  })
  for (const s of type.needs ?? []) push(s, true, null)
  for (const s of type.needsAny ?? []) push(s, false, 'any')
  for (const s of type.wants ?? []) push(s, false, null)
  return rows
}
